import { useState, useMemo, useRef, useCallback } from "react";

import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { Notifications } from "@atlaskit/navigation-system/top-nav-items";
import { NotificationIndicator } from "@atlaskit/notification-indicator";
import { NotificationLogClient } from "@atlaskit/notification-log-client";
import Popup from "@atlaskit/popup";

import { wacToken } from "@/libraries/design/foundations";
import { isProduction } from "@/utils/urls";

import { NotificationsDrawer } from "./notifications-drawer";

// override default Notification icon button styles to match WAC global nav styles
const iconStyles = css({
    marginInlineEnd: 16,

    //eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    button: {
        "backgroundColor": "transparent",
        "&::after": {
            border: "none !important"
        },
        "&:hover, &:active": {
            backgroundColor: "transparent",
            border: "none",
            //eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            svg: {
                color: wacToken("color.primary.blue")
            }
        }
    },

    //eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    svg: {
        height: 22,
        width: 22,
        color: wacToken("color.primary.black")
    }
});

const notificationLogUrl = isProduction()
    ? "/gateway/api/notification-log"
    : "https://wac.stg.internal.atlassian.com/gateway/api/notification-log";
const useNotificationLogClient = () => {
    return useMemo(
        () => new NotificationLogClient(notificationLogUrl, undefined),
        []
    );
};
const REFRESH_INTERVAL = 60000;

class MockNotificationLogClient extends NotificationLogClient {
    public mockedCount = 0;

    public constructor(mockedCount: number) {
        super("", "");
        this.mockedCount = mockedCount;
    }

    public override countUnseenNotifications() {
        return Promise.resolve({ count: this.mockedCount });
    }
}

const emptyNotificationLogClient = new MockNotificationLogClient(0);

export const NotificationsComponent = () => {
    const notificationsButtonRef = useRef<HTMLButtonElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isCleared, setIsCleared] = useState(false);

    const notificationLogClient = useNotificationLogClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const NotificationsBadge = useMemo(
        //eslint-disable-next-line react/no-unstable-nested-components
        () => () => {
            return (
                <NotificationIndicator
                    appearance="important"
                    max={9}
                    notificationLogProvider={Promise.resolve(
                        isCleared || isOpen
                            ? emptyNotificationLogClient
                            : notificationLogClient
                    )}
                    refreshRate={REFRESH_INTERVAL}
                    ssrInitialValue={undefined}
                />
            );
        },
        [isOpen, isCleared, notificationLogClient]
    );

    const onClick = () => {
        const action = isOpen ? "closed" : "opened";
        createAnalyticsEvent({
            eventName: "buttonClicked",
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "notificationsButton",
            source: "globalNav",
            attributes: {
                action
            }
        }).fire();

        if (isOpen) {
            closeNotificationsPopup();
        } else {
            setIsCleared(true);
            setIsOpen(true);
        }
        return;
    };

    const closeNotificationsPopup = useCallback(() => {
        notificationsButtonRef.current?.focus();
        setIsOpen(false);
    }, []);

    return (
        <>
            <Popup
                placement="bottom-end"
                shouldFlip
                rootBoundary="viewport"
                //eslint-disable-next-line react/no-unstable-nested-components
                content={() => (
                    <NotificationsDrawer
                        notificationLogUrl={notificationLogUrl}
                    />
                )}
                isOpen={isOpen}
                onClose={closeNotificationsPopup}
                shouldRenderToParent
                //eslint-disable-next-line react/no-unstable-nested-components
                trigger={(triggerProps) => (
                    <div css={iconStyles}>
                        <Notifications
                            {...triggerProps}
                            badge={NotificationsBadge}
                            onClick={onClick}
                            isSelected={isOpen}
                            label="Notifications"
                        />
                    </div>
                )}
            />
        </>
    );
};

export default NotificationsComponent;
